import IconExportUpload from "@synthesis/shared-icons/IconExportUpload";
import {
  exportExternalOperatorRequestZip,
  exportExternalOperatorRequestsZip,
  exportInternalResultZip,
  exportInternalRequestsZip,
} from "@synthesis/shared-services";
import {
  getCountsQueryKey as getExternalCountsQueryKey,
  getOperatorCountsQueryKey,
  getOperatorRequestsQueryKey,
} from "@synthesis/shared-services/external/application/hooks";
import {
  getCountsQueryKey as getInternalCountsQueryKey,
  getRequestsQueryKey as getInternalRequestsQueryKey,
} from "@synthesis/shared-services/internal/application/hooks";
import { Button, setPromiseSnack, setSnack } from "@synthesis/shared-ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useClientType } from "../../shared/hooks/use-client-type";
import { useUserRoles } from "../../shared/hooks/use-user-roles";
import { saveBlob } from "../../shared/utils/save-blob";
import { type ExportApplicationItem, getBatchExportFileName } from "./export-application.lib";

export type ExportApplicationButtonProps = {
  applications: ExportApplicationItem[];
  className?: string;
  disabled?: boolean;
  onExported?: (exportedIds: string[]) => void | Promise<void>;
};

export const ExportApplicationButton = ({ applications, className, disabled: disabledProp, onExported }: ExportApplicationButtonProps) => {
  const clientType = useClientType();
  const userRoles = useUserRoles();
  const queryClient = useQueryClient();
  const exportOperatorMutation = useMutation({ mutationFn: (id: string) => exportExternalOperatorRequestZip(id) });
  const exportInternalMutation = useMutation({ mutationFn: (id: string) => exportInternalResultZip(id) });
  const exportOperatorBatchMutation = useMutation({ mutationFn: (ids: string[]) => exportExternalOperatorRequestsZip(ids) });
  const exportInternalBatchMutation = useMutation({ mutationFn: (ids: string[]) => exportInternalRequestsZip(ids) });

  const isOperator = (clientType === "external" && userRoles.isExternalOperator) || (clientType === "internal" && userRoles.isInternalOperator);

  const selectedCount = applications.length;
  const isExportPending =
    exportOperatorMutation.isPending ||
    exportInternalMutation.isPending ||
    exportOperatorBatchMutation.isPending ||
    exportInternalBatchMutation.isPending;
  const isDisabled = disabledProp || selectedCount === 0 || isExportPending;

  const handleClick = () => {
    const ids = applications.map((application) => application.id).filter((id): id is string => Boolean(id));

    if (ids.length === 0) {
      setSnack({
        title: "Экспорт заявок",
        description: selectedCount > 1 ? "Не удалось определить заявки" : "Не удалось определить заявку",
        messageType: "error",
      });
      return;
    }

    const isBatchExport = ids.length > 1;
    const application = applications[0]!;

    const promise = (async () => {
      const blob =
        clientType === "internal"
          ? isBatchExport
            ? await exportInternalBatchMutation.mutateAsync(ids)
            : await exportInternalMutation.mutateAsync(ids[0]!)
          : isBatchExport
            ? await exportOperatorBatchMutation.mutateAsync(ids)
            : await exportOperatorMutation.mutateAsync(ids[0]!);

      saveBlob(
        blob,
        isBatchExport ? getBatchExportFileName(ids.length, clientType) : (application.exportFileName ?? `zayavka-${application.id}.zip`),
      );

      await queryClient.invalidateQueries({
        queryKey: clientType === "internal" ? getInternalRequestsQueryKey() : getOperatorRequestsQueryKey(),
      });
      await queryClient.invalidateQueries({ queryKey: getInternalCountsQueryKey() });
      await queryClient.invalidateQueries({ queryKey: getExternalCountsQueryKey() });
      await queryClient.invalidateQueries({ queryKey: getOperatorCountsQueryKey() });

      await onExported?.(ids);
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: {
          pending: isBatchExport ? "Экспорт заявок" : "Экспорт заявки",
          fulfilled: isBatchExport ? "Заявки экспортированы" : "Заявка экспортирована",
          rejected: isBatchExport ? "Не удалось экспортировать заявки" : "Не удалось экспортировать заявку",
        },
      },
    });
  };

  if (!isOperator) {
    return null;
  }

  return (
    <Button
      type="button"
      theme="secondary"
      size="sm"
      leftIcon={<IconExportUpload className="size-5" />}
      className={className}
      disabled={isDisabled}
      onClick={handleClick}
    >
      Экспортировать
    </Button>
  );
};
